import React, { useState } from "react";
import axios from "axios";
import { MapPin, Phone, EnvelopeSimple, Clock, ArrowRight, CheckCircle } from "@phosphor-icons/react";
import { useLang } from "../i18n/LanguageContext";
import SEO from "../components/SEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Contact = () => {
  const { t } = useLang();
  const [form, setForm] = useState({ name: "", company: "", email: "", phone: "", message: "" });
  const [status, setStatus] = useState({ loading: false, ok: false, error: null });

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setStatus({ loading: true, ok: false, error: null });
    try {
      await axios.post(`${API}/contact`, form);
      setStatus({ loading: false, ok: true, error: null });
      setForm({ name: "", company: "", email: "", phone: "", message: "" });
    } catch (err) {
      setStatus({ loading: false, ok: false, error: t.contact.error });
    }
  };

  return (
    <>
      <SEO
        title="Contact"
        description="Contact Masika Chem SARL — No. 44 Avenue Sendwe, Lubumbashi, D.R. Congo. +243 817 106 217. sales@masikachem.com."
        path="/contact"
      />

      <section className="bg-white" data-testid="contact-hero">
        <div className="max-w-[1400px] mx-auto px-5 lg:px-10 pt-20 pb-12 grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-7">
            <div className="inline-flex items-center gap-3 mb-4">
              <span className="w-10 h-px bg-[#C8102E]" />
              <span className="text-[11px] font-bold tracking-[0.3em] uppercase text-[#C8102E]">
                {t.contact.overline}
              </span>
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold leading-[1.05] tracking-tight text-neutral-900">
              {t.contact.title}
            </h1>
          </div>
          <div className="md:col-span-5 flex items-end">
            <p className="text-lg text-neutral-600 leading-relaxed">{t.contact.sub}</p>
          </div>
        </div>
      </section>

      <section className="bg-white pb-24" data-testid="contact-body">
        <div className="max-w-[1400px] mx-auto px-5 lg:px-10">
          <div className="grid grid-cols-1 md:grid-cols-12 border border-neutral-200">
            {/* FORM */}
            <div className="md:col-span-7 p-8 lg:p-12 border-b md:border-b-0 md:border-r border-neutral-200">
              {status.ok ? (
                <div
                  data-testid="contact-success"
                  className="flex flex-col items-start gap-5 py-10"
                >
                  <CheckCircle size={48} weight="duotone" className="text-[#16A34A]" />
                  <h3 className="text-2xl font-extrabold text-neutral-900 tracking-tight">
                    {t.contact.success}
                  </h3>
                  <button
                    onClick={() => setStatus({ loading: false, ok: false, error: null })}
                    data-testid="contact-send-another"
                    className="text-sm font-bold text-[#C8102E] inline-flex items-center gap-1"
                  >
                    Send another message <ArrowRight size={12} weight="bold" />
                  </button>
                </div>
              ) : (
                <form onSubmit={onSubmit} className="space-y-5" data-testid="contact-form">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <Field name="name" label={t.contact.formName} value={form.name} onChange={onChange} required />
                    <Field name="company" label={t.contact.formCompany} value={form.company} onChange={onChange} required />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <Field name="email" type="email" label={t.contact.formEmail} value={form.email} onChange={onChange} required />
                    <Field name="phone" label={t.contact.formPhone} value={form.phone} onChange={onChange} />
                  </div>
                  <div>
                    <label className="block text-[11px] font-bold tracking-[0.2em] uppercase text-neutral-700 mb-2">
                      {t.contact.formMessage}
                    </label>
                    <textarea
                      name="message"
                      value={form.message}
                      onChange={onChange}
                      data-testid="contact-message"
                      required
                      rows={5}
                      className="w-full border border-neutral-300 px-4 py-3 text-sm focus:outline-none focus:border-[#C8102E] bg-white"
                    />
                  </div>

                  {status.error && (
                    <div className="text-sm text-[#C8102E] font-medium" data-testid="contact-error">
                      {status.error}
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={status.loading}
                    data-testid="contact-submit"
                    className="inline-flex items-center gap-2 bg-[#C8102E] hover:bg-[#A00D25] text-white px-7 py-4 font-semibold disabled:opacity-60"
                  >
                    {status.loading ? t.common.sending : t.contact.formSubmit}
                    <ArrowRight size={18} weight="bold" />
                  </button>
                </form>
              )}
            </div>

            {/* INFO */}
            <div className="md:col-span-5 bg-[#0E0E0E] text-white p-8 lg:p-12">
              <h3 className="text-2xl font-extrabold tracking-tight mb-10">Get in touch directly</h3>
              <ul className="space-y-7">
                <InfoLine icon={MapPin} label={t.contact.address} value={t.contact.addressValue} />
                <InfoLine
                  icon={Phone}
                  label={t.contact.phone}
                  value={(
                    <>
                      <a href="tel:+243817106217" className="block hover:text-[#E85A1F]">+243 817 106 217</a>
                      <a href="tel:+243833077650" className="block hover:text-[#E85A1F]">+243 833 077 650</a>
                    </>
                  )}
                />
                <InfoLine
                  icon={EnvelopeSimple}
                  label={t.contact.email}
                  value={(
                    <>
                      <a href="mailto:sales@masikachem.com" className="block hover:text-[#E85A1F]">sales@masikachem.com</a>
                      <a href="mailto:saleskol@masikachem.com" className="block hover:text-[#E85A1F]">saleskol@masikachem.com</a>
                    </>
                  )}
                />
                <InfoLine icon={Clock} label={t.contact.hours} value={t.contact.hoursValue} />
              </ul>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

const Field = ({ name, label, type = "text", value, onChange, required }) => (
  <div>
    <label className="block text-[11px] font-bold tracking-[0.2em] uppercase text-neutral-700 mb-2">
      {label}{required ? " *" : ""}
    </label>
    <input
      name={name}
      type={type}
      value={value}
      onChange={onChange}
      required={required}
      data-testid={`contact-${name}`}
      className="w-full border border-neutral-300 px-4 py-3 text-sm focus:outline-none focus:border-[#C8102E] bg-white"
    />
  </div>
);

const InfoLine = ({ icon: Icon, label, value }) => (
  <li className="flex items-start gap-4">
    <div className="w-10 h-10 bg-[#C8102E]/15 text-[#E85A1F] flex items-center justify-center shrink-0">
      <Icon size={18} weight="duotone" />
    </div>
    <div>
      <div className="text-[10px] font-bold tracking-[0.3em] uppercase text-neutral-400 mb-1">{label}</div>
      <div className="text-sm text-neutral-100 leading-relaxed">{value}</div>
    </div>
  </li>
);

export default Contact;
